import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getLoyalty, refreshLoyalty, getCrmGuestAnalytics, getCrmEngagementAnalytics, getCrmProgramAnalytics,
} from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { formatMoney } from '../../utils/formatters';
import KpiTiles, { ProgramKpiTiles, RiskStrip } from './analytics/KpiTiles';
import MonthlyChart from './analytics/MonthlyChart';
import SegmentsBlock from './analytics/SegmentsBlock';
import NewcomersBlock from './analytics/NewcomersBlock';
import EngagementBlock from './analytics/EngagementBlock';
import ProgramBlock from './analytics/ProgramBlock';
import { PERIOD_LABELS, fmtPct } from './analytics/shared';
import { CalendarIcon, WarningIcon, RefreshIcon } from './analytics/icons';
import './AnalyticsTab.css';

// Два источника с разными периодами. Программа лояльности iiko.biz отдаёт
// траты за скользящие 7/30/365 дней, карты OLAP — любое окно.
const CARD_PERIODS = [30, 90, 365];
const PROGRAM_PERIODS = [7, 30, 365];
const PROGRAM_LABELS = { 7: 'Неделя', 30: 'Месяц', 365: 'Год' };
const CARD_PERIOD_KEY = 'crm-analytics-period';
const PROGRAM_PERIOD_KEY = 'crm-analytics-program-period';

const readPeriod = (key, allowed, fallback) => {
  try {
    const v = Number(window.localStorage.getItem(key));
    return allowed.includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
};

const savePeriod = (key, value) => {
  try { window.localStorage.setItem(key, String(value)); } catch { /* приватный режим */ }
};

const fmtDate = (iso) => new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });

// Даты для <input type="date"> — по местному времени, не UTC
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return isoDay(d); };
const spanDays = (range) => Math.round((new Date(range.to) - new Date(range.from)) / 86400000) + 1;

// С какой даты есть данные: журнал визитов программы — с 01.01.2025; карты —
// выгрузка за 3 года, из которых год нужен как история перед окном.
const PROGRAM_MIN_DATE = '2025-01-01';
const CARD_HISTORY_DAYS = 730;

/**
 * Свой период: две даты и «Показать». Сравнение — с таким же числом дней
 * прямо перед периодом.
 */
const CustomRange = ({ value, minDate, onApply, onCancel }) => {
  const today = isoDay(new Date());
  const [from, setFrom] = useState(value?.from || daysAgo(13));
  const [to, setTo] = useState(value?.to || today);
  const valid = from && to && from <= to && from >= minDate && to <= today;
  return (
    <form
      className="crma-range"
      onSubmit={(e) => { e.preventDefault(); if (valid) onApply({ from, to }); }}
    >
      <label>
        <span>с</span>
        <input type="date" value={from} min={minDate} max={to || today} onChange={(e) => setFrom(e.target.value)} required />
      </label>
      <label>
        <span>по</span>
        <input type="date" value={to} min={from || minDate} max={today} onChange={(e) => setTo(e.target.value)} required />
      </label>
      <button type="submit" className="ui-btn-primary" disabled={!valid}>Показать</button>
      <button type="button" className="ui-btn-ghost" onClick={onCancel}>Отмена</button>
      <span className="crma-dim">данные есть с {fmtDate(minDate)}</span>
    </form>
  );
};

/**
 * Аналитика CRM. Порядок блоков — от «что происходит» к «что делать»:
 * главные числа периода → кого теряем → сегменты (с переходом в список
 * гостей) → новички → бонусы и охват → работает ли работа с гостями.
 *
 * Режим выбирает сервер: если у организации есть программа лояльности
 * iiko.biz (/crm/analytics/program → available), всё считается по её
 * гостям — это реальные люди и почти вся выручка. Иначе — по картам OLAP
 * (/api/loyalty), как раньше.
 *
 * Загрузки независимые: ошибка или задержка одной не прячет другие —
 * однажды так уже пропадали целые блоки, вложенные в условие чужой загрузки.
 */
const AnalyticsTab = ({ onOpenGuests, onOpenOffers }) => {
  const [mode, setMode] = useState(null);   // null — ещё выясняем, 'program' | 'cards'
  const [programDays, setProgramDays] = useState(() => readPeriod(PROGRAM_PERIOD_KEY, PROGRAM_PERIODS, 30));
  const [program, setProgram] = useState(null);
  const [programLoading, setProgramLoading] = useState(true);
  const [programError, setProgramError] = useState('');
  const [cardDays, setCardDays] = useState(() => readPeriod(CARD_PERIOD_KEY, CARD_PERIODS, 365));
  const [loyalty, setLoyalty] = useState(null);      // ответ /api/loyalty целиком
  const [loyaltyError, setLoyaltyError] = useState('');
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [guestAnalytics, setGuestAnalytics] = useState(null);
  const [engagement, setEngagement] = useState(null);
  // Свой период { from, to } — общий для обоих режимов; null — готовое окно
  const [custom, setCustom] = useState(null);
  const [rangeOpen, setRangeOpen] = useState(false);
  const requestSeq = useRef(0);

  // Быстрое переключение периодов: ответ на старый запрос не должен
  // перетереть более новый — общий счётчик для обоих источников.
  const loadProgram = useCallback(async (period) => {
    const seq = ++requestSeq.current;
    setProgramLoading(true);
    try {
      const res = await getCrmProgramAnalytics(period);
      if (seq !== requestSeq.current) return;
      setProgramError('');
      if (res.available) {
        setProgram(res);
        setMode('program');
      } else {
        setMode('cards');
      }
    } catch (err) {
      if (seq !== requestSeq.current) return;
      if (err.response?.status === 400) {
        // неверный свой период — показываем причину, режим не меняем
        setProgramError(err.response.data?.error || 'Неверный период');
      } else {
        // эндпоинт недоступен (например, у роли нет прав) — остаёмся на картах
        setMode((m) => m || 'cards');
      }
    } finally {
      if (seq === requestSeq.current) setProgramLoading(false);
    }
  }, []);

  const loadLoyalty = useCallback(async (period) => {
    const seq = ++requestSeq.current;
    setLoyaltyLoading(true);
    try {
      const res = await getLoyalty(period);
      if (seq !== requestSeq.current) return;
      setLoyalty(res);
      setLoyaltyError('');
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setLoyaltyError(err.response?.data?.error || 'Не удалось загрузить данные лояльности');
    } finally {
      if (seq === requestSeq.current) setLoyaltyLoading(false);
    }
  }, []);

  const programPeriod = custom || programDays;
  const cardPeriod = custom || cardDays;

  // Сначала спрашиваем программу; карты грузим, только если её нет.
  useEffect(() => { if (mode !== 'cards') loadProgram(programPeriod); }, [programDays, custom, loadProgram]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (mode === 'cards') loadLoyalty(cardPeriod); }, [mode, cardDays, custom, loadLoyalty]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    (async () => {
      const [guestsRes, engagementRes] = await Promise.allSettled([
        getCrmGuestAnalytics(), getCrmEngagementAnalytics(),
      ]);
      if (guestsRes.status === 'fulfilled') setGuestAnalytics(guestsRes.value);
      if (engagementRes.status === 'fulfilled') setEngagement(engagementRes.value);
    })();
  }, []);

  const isProgram = mode === 'program';
  const periods = isProgram ? PROGRAM_PERIODS : CARD_PERIODS;
  const days = custom ? null : (isProgram ? programDays : cardDays);
  const customDays = custom ? spanDays(custom) : null;
  // Подпись к стрелкам: для своего периода — такое же число дней перед ним
  const vsLabel = custom ? `к предыдущим ${customDays} дн.` : undefined;
  const minDate = isProgram ? PROGRAM_MIN_DATE : daysAgo(CARD_HISTORY_DAYS);

  const pickPeriod = (p) => {
    setCustom(null);
    setRangeOpen(false);
    if (isProgram) {
      setProgramDays(p);
      savePeriod(PROGRAM_PERIOD_KEY, p);
    } else {
      setCardDays(p);
      savePeriod(CARD_PERIOD_KEY, p);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      if (isProgram) {
        await loadProgram(programPeriod);   // данные iiko.biz сервер обновляет сам каждые 5 минут
      } else {
        await refreshLoyalty();
        await loadLoyalty(cardPeriod);
      }
    } finally {
      setRefreshing(false);
    }
  };

  const data = loyalty?.data;
  const previous = loyalty?.previous;
  const summary = program?.summary;
  const period = isProgram ? summary?.period : data?.period;
  const busy = isProgram ? programLoading : loyaltyLoading;
  const firstLoad = mode === null || (isProgram ? !program : loyaltyLoading && !loyalty);
  const openGuests = onOpenGuests || (() => {});

  return (
    <div className="crma">
      <div className="crma-toolbar">
        <div className="crma-segmented" role="tablist" aria-label="Период">
          {mode && periods.map((p) => (
            <button
              key={p}
              role="tab"
              aria-selected={days === p}
              className={days === p ? 'active' : ''}
              onClick={() => pickPeriod(p)}
            >
              {isProgram ? PROGRAM_LABELS[p] : PERIOD_LABELS[p]}
            </button>
          ))}
          {mode && (
            <button
              role="tab"
              aria-selected={!!custom}
              className={custom || rangeOpen ? 'active' : ''}
              onClick={() => setRangeOpen((v) => !v)}
            >
              <CalendarIcon size={14} /> Свой период
            </button>
          )}
        </div>
        <div className="crma-period">
          <CalendarIcon size={15} />
          {period ? `${fmtDate(period.from)} — ${fmtDate(period.to)}` : '—'}
          {isProgram ? (
            <span className="crma-dim">· программа лояльности iiko.biz</span>
          ) : loyalty?.last_update && (
            <span className="crma-dim">
              · обновлено {new Date(loyalty.last_update).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
        <button className="ui-btn-ghost crma-refresh" onClick={handleRefresh} disabled={refreshing || !mode}>
          <span className={refreshing ? 'crma-spin' : ''}><RefreshIcon size={15} /></span>
          {refreshing ? 'Считаем…' : 'Обновить'}
        </button>
      </div>

      {rangeOpen && mode && (
        <CustomRange
          value={custom}
          minDate={minDate}
          onApply={(range) => { setCustom(range); setRangeOpen(false); }}
          onCancel={() => setRangeOpen(false)}
        />
      )}

      {firstLoad && <LoadingSpinner />}

      {isProgram && programError && (
        <div className="crma-error">
          <WarningIcon size={18} />
          <div><strong>{programError}</strong></div>
        </div>
      )}

      {/* Свой период считается по журналу визитов — пока он грузится, чисел нет */}
      {isProgram && program?.custom && !summary && !programError && (
        <div className="crma-empty panel">
          Свой период считается по истории визитов, а она ещё загружается
          {program.journal?.total ? ` (${program.journal.loaded} из ${program.journal.total} гостей)` : ''}.
          Пока доступны неделя, месяц и год.
        </div>
      )}

      {mode === 'cards' && !loyaltyLoading && loyaltyError && (
        <div className="crma-error">
          <WarningIcon size={18} />
          <div>
            <strong>{loyaltyError}</strong>
            <p>Расчёт идёт по трём годам истории и может занять до минуты — попробуйте обновить чуть позже.</p>
          </div>
        </div>
      )}

      {mode === 'cards' && loyalty && !data && !loyaltyError && (
        <div className="crma-empty panel">За этот период нет визитов гостей с картой.</div>
      )}

      {isProgram && summary && (
        <ProgramKpiTiles
          summary={summary}
          previous={program.previous}
          currentJournal={program.current_journal}
          journal={program.journal}
          days={programDays}
          vsLabel={program.custom ? `к предыдущим ${program.days} дн.` : undefined}
          busy={busy}
        />
      )}
      {mode === 'cards' && data && (
        <KpiTiles data={data} previous={previous} days={cardDays} vsLabel={loyalty?.custom ? vsLabel : undefined} busy={busy} />
      )}

      {mode && <RiskStrip summary={guestAnalytics?.status_summary} onOpenGuests={openGuests} />}

      {mode === 'cards' && data && (
        <MonthlyChart monthly={data.monthly} period={data.monthly_period || data.period} key={`m-${cardDays}`} />
      )}
      {/* В режиме программы — из журнала визитов, когда история загружена */}
      {isProgram && summary && program.monthly && program.monthly.length > 1 && (
        <MonthlyChart
          monthly={program.monthly}
          period={{ from: `${program.monthly[0].month}-01`, to: summary.period.to }}
          sub="Сколько разных гостей программы лояльности пришло в каждом месяце — и сколько из них впервые"
          newLabel="Новые — первый заказ в программе"
        />
      )}

      {mode && (
        <SegmentsBlock
          rfm={isProgram ? program.rfm : guestAnalytics?.rfm || null}
          rfmSub={isProgram
            ? 'RFM по гостям программы: давность последнего визита, число заказов и траты за последние 365 дней. Нажмите «Гости», чтобы открыть список сегмента и поставить задачи'
            : undefined}
          frequency={isProgram ? null : data?.segments || null}
          tiers={isProgram ? program.tiers : null}
          topSpenders={guestAnalytics?.top_spenders || []}
          topSource={guestAnalytics?.top_source}
          onOpenGuests={openGuests}
          onOpenOffers={onOpenOffers}
        />
      )}

      {isProgram && summary && (
        <NewcomersBlock
          newcomers={summary.newcomers}
          previous={program.previous?.newcomers}
          deltaBase={program.current_journal?.newcomers}
          prevLabel={program.custom
            ? `к предыдущим ${program.days} дн.`
            : { 7: 'к прошлым 7 дням', 30: 'к прошлым 30 дням', 365: 'к прошлому году' }[programDays]}
          cohorts={program.cohorts || []}
          days={programDays}
          definition="Новый гость — первый заказ в программе лояльности пришёлся на этот период"
          periodNote={(program.custom ? program.days : programDays) <= 7
            ? 'За неделю у новичков почти не было времени вернуться — смотрите месяц или год.'
            : 'Вернулся — пришёл ещё хотя бы в один другой день. На коротком периоде доля ниже: у гостя было меньше времени вернуться.'}
          firstVisitNote="Блок ниже пока считается по картам iiko: ему нужен состав заказов, которого нет в данных программы."
        />
      )}
      {mode === 'cards' && data && (
        <NewcomersBlock
          newcomers={data.newcomers}
          previous={previous?.newcomers}
          prevLabel={loyalty?.custom ? vsLabel : undefined}
          cohorts={data.cohorts || []}
          days={cardDays}
        />
      )}

      {isProgram && <ProgramBlock balance={program.balance} reach={program.reach} onOpenGuests={openGuests} />}

      {mode && engagement && <EngagementBlock engagement={engagement} />}

      {isProgram ? (
        guestAnalytics?.program_coverage && (
          <p className="crma-footnote">
            Все блоки, кроме «Что в первом визите», считаются по гостям программы лояльности iiko.biz.
            За последние 365 дней они потратили {formatMoney(guestAnalytics.program_coverage.program_revenue)} —
            около {guestAnalytics.program_coverage.percent}% всей выручки. Данные обновляются каждые 5 минут.
          </p>
        )
      ) : data?.coverage && (
        <p className="crma-footnote">
          Все цифры — по гостям с картой лояльности: это {fmtPct(data.coverage.identified_percent)} выручки.
          Ещё {formatMoney(data.coverage.anonymous_revenue)} прошло по служебной карте без опознания гостя.
        </p>
      )}
    </div>
  );
};

export default AnalyticsTab;
